import { isExplainChangeEnabled } from '@thoughtspot/session-service';
import { useCallback, useMemo, useState } from 'react';
import { Key } from 'w3c-keys';
import { ChartEventTypes } from '../../..';
import { getTranslatedTextCallback } from '../../../env';
import { logger } from './constants';
export function useChangeAnalysis(model, input) {
    var _a, _b, _c, _d, _e, _f;
    const { chartModel, env, config, chartObj, dataModel, eventsMap } = input;
    const { effectiveRefTuple, currentRefTuple, isChartTimeSeries, isSinglePointKPIEnabled, showAnalyseCTA, } = model;
    const [isCCAErrorModalOpen, setIsCCAErrorModalOpen] = useState(false);
    const hasAccessPermission = (_a = chartObj === null || chartObj === void 0 ? void 0 : chartObj.chartOptions) === null || _a === void 0 ? void 0 : _a.hasAccessPermission;
    const hideAnalyzeButton = (_b = chartObj === null || chartObj === void 0 ? void 0 : chartObj.chartOptions) === null || _b === void 0 ? void 0 : _b.hideAnalyzeButton;
    const shouldHideAnalyzeCTAForEmbed = (_c = chartObj === null || chartObj === void 0 ? void 0 : chartObj.chartOptions) === null || _c === void 0 ? void 0 : _c.shouldHideAnalyzeCTAForEmbed;
    const { isDisabled: isAnalyzeButtonDisabledFromEmbed = false, disableReason: analyzeButtonDisableReasonFromEmbed = '', } = ((_d = chartObj === null || chartObj === void 0 ? void 0 : chartObj.chartOptions) === null || _d === void 0 ? void 0 : _d.kpiAnalysisButtonDisabledForEmbed) || {};
    const isPrintMode = !!((_e = chartObj === null || chartObj === void 0 ? void 0 : chartObj.chartOptions) === null || _e === void 0 ? void 0 : _e.isPrintMode);
    const isAnomalyV2Enabled = !!(((_f = env === null || env === void 0 ? void 0 : env.flags) === null || _f === void 0 ? void 0 : _f.isAnomalyV2Enabled) && !shouldHideAnalyzeCTAForEmbed);
    const isExplainChangeFeatureEnabled = !!(isExplainChangeEnabled() &&
        (config === null || config === void 0 ? void 0 : config.shouldShowExplainChange) &&
        hasAccessPermission);
    const isEmptyComparison = !effectiveRefTuple || !effectiveRefTuple.time;
    const isAnalyzeButtonEnabled = isExplainChangeFeatureEnabled && !isAnalyzeButtonDisabledFromEmbed;
    const tooltip = useMemo(() => {
        let tooltipContent = '';
        if (!isEmptyComparison) {
            if (isAnalyzeButtonDisabledFromEmbed) {
                tooltipContent = analyzeButtonDisableReasonFromEmbed;
            }
            else {
                const getText = getTranslatedTextCallback();
                if (!isAnomalyV2Enabled && isExplainChangeFeatureEnabled) {
                    tooltipContent = getText('RUN_CHANGE_ANALYSIS');
                }
                else if (!hasAccessPermission) {
                    tooltipContent = getText('CHANGE_ANALYSIS_PERMISSION_UNDERLYING_DATA_ACCESS');
                }
            }
        }
        return {
            'data-tooltip-content': tooltipContent,
            'data-tooltip-position': 'top',
            'data-tooltip-align': isAnomalyV2Enabled ? 'left' : 'right',
        };
    }, [
        isEmptyComparison,
        isAnalyzeButtonDisabledFromEmbed,
        analyzeButtonDisableReasonFromEmbed,
        isAnomalyV2Enabled,
        isExplainChangeFeatureEnabled,
        hasAccessPermission,
    ]);
    const showAnalyzeButton = isAnomalyV2Enabled &&
        (isChartTimeSeries || isSinglePointKPIEnabled) &&
        showAnalyseCTA &&
        !hideAnalyzeButton;
    const onChangeAnalysisClick = useCallback(() => {
        if (!dataModel) {
            logger.warn('Cannot analyze change: dataModel is undefined');
            return;
        }
        const onChangeAnalysis = eventsMap === null || eventsMap === void 0 ? void 0 : eventsMap.get(ChartEventTypes.ChangeAnalysisClick);
        if (onChangeAnalysis) {
            onChangeAnalysis(chartModel, dataModel.getHighchartDatamodel(), currentRefTuple, effectiveRefTuple);
        }
    }, [dataModel, eventsMap, chartModel, currentRefTuple, effectiveRefTuple]);
    const handleKeyPress = useCallback((event) => {
        event.stopPropagation();
        if (event.key === Key.Enter || event.key === Key.Space) {
            onChangeAnalysisClick();
        }
    }, [onChangeAnalysisClick]);
    return {
        showAnalyzeButton,
        isAnalyzeButtonEnabled,
        isEmptyComparison,
        isAnomalyV2Enabled,
        isPrintMode,
        tooltip,
        onChangeAnalysisClick,
        handleKeyPress,
        isCCAErrorModalOpen,
        setIsCCAErrorModalOpen,
    };
}
//# sourceMappingURL=use-change-analysis.js.map